export const BLOCKED_JOIN_EVENT_TYPES = [
  "viewed_full",
  "join_rejected",
] as const;

export type BlockedJoinEventType = (typeof BLOCKED_JOIN_EVENT_TYPES)[number];

export const VIEWED_FULL_DEDUPE_WINDOW_MS = 30 * 60 * 1000;

export const BLOCKED_JOIN_EVENT_LIST_LIMIT = 200;

/**
 * Accounting for get_blocked_joins. Historical rows were deduped; new rows
 * are one viewed_full per page view and are not backfilled.
 */
export const BLOCKED_JOIN_VIEW_ACCOUNTING = {
  viewedFull: "one_row_per_page_view",
  historicalDedupeWindowMs: VIEWED_FULL_DEDUPE_WINDOW_MS,
  listLimit: BLOCKED_JOIN_EVENT_LIST_LIMIT,
  note: "Each viewed_full row is one full-party page view. Rows stored before dedupe was removed kept at most one viewed_full per actor inside historicalDedupeWindowMs and were not backfilled. PostHog party_invite_viewed_full matches new rows. The events list is the newest listLimit rows; totals are stored row counts.",
} as const;

const VISITOR_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isBlockedJoinEventType(
  value: string,
): value is BlockedJoinEventType {
  return (BLOCKED_JOIN_EVENT_TYPES as readonly string[]).includes(value);
}

export function isVisitorId(value: string | null | undefined): value is string {
  return Boolean(value && VISITOR_ID_RE.test(value));
}

export function blockedJoinActorKey(input: {
  userId?: string | null;
  visitorId: string;
}): string {
  const userId = input.userId?.trim();
  if (userId) {
    return `user:${userId}`;
  }
  return `visitor:${input.visitorId}`;
}

/**
 * Historical rule used until viewed_full inserts stopped being deduped.
 * A repeat full-party view from the same actor inside this window was
 * dropped and was not sent to PostHog. New rows are one per page view.
 * This helper stays so the old window remains documented and testable.
 */
export function shouldSkipDuplicateViewedFull(
  previousCreatedAt: Date | string,
  now: Date = new Date(),
  windowMs: number = VIEWED_FULL_DEDUPE_WINDOW_MS,
): boolean {
  const previous =
    previousCreatedAt instanceof Date
      ? previousCreatedAt
      : new Date(previousCreatedAt);
  if (Number.isNaN(previous.getTime())) {
    return false;
  }
  return now.getTime() - previous.getTime() < windowMs;
}

export interface BlockedJoinEventCounts {
  viewedFull: number;
  joinRejected: number;
  total: number;
}

export function blockedJoinEventCounts(input: {
  viewedFull?: number | null;
  joinRejected?: number | null;
}): BlockedJoinEventCounts {
  const viewedFull = Math.max(0, Number(input.viewedFull) || 0);
  const joinRejected = Math.max(0, Number(input.joinRejected) || 0);
  return {
    viewedFull,
    joinRejected,
    total: viewedFull + joinRejected,
  };
}

export interface BlockedJoinEventDto {
  id: string;
  partyId: string;
  type: BlockedJoinEventType;
  userId: string | null;
  visitorId: string;
  createdAt: string;
}

export const BLOCKED_JOIN_POSTHOG_EVENTS = {
  viewed_full: "party_invite_viewed_full",
  join_rejected: "party_join_rejected",
} as const;

export type BlockedJoinPostHogEventName =
  (typeof BLOCKED_JOIN_POSTHOG_EVENTS)[BlockedJoinEventType];

import { resolvePostHogDistinctId } from "./posthog-identity";

function isRealClerkUserId(value: string | null | undefined): value is string {
  const clerkUserId = value?.trim() ?? "";
  return (
    clerkUserId.length > 0 &&
    resolvePostHogDistinctId({ clerkUserId }) === clerkUserId
  );
}

export function blockedJoinPostHogDistinctId(input: {
  clerkUserId?: string | null;
  visitorId: string;
  postHogDistinctId?: string | null;
}): string {
  const postHogDistinctId = input.postHogDistinctId?.trim() ?? "";
  return (
    resolvePostHogDistinctId({
      clerkUserId: input.clerkUserId,
      anonymousDistinctId: postHogDistinctId || input.visitorId,
    }) ?? input.visitorId
  );
}

export function buildBlockedJoinPostHogCapture(input: {
  type: BlockedJoinEventType;
  partyId: string;
  visitorId: string;
  userId?: string | null;
  clerkUserId?: string | null;
  postHogDistinctId?: string | null;
}): {
  distinctId: string;
  event: BlockedJoinPostHogEventName;
  properties: {
    party_id: string;
    event_type: BlockedJoinEventType;
    visitor_id: string;
    user_id: string | null;
    db_user_id: string | null;
  };
} {
  const clerkUserId = isRealClerkUserId(input.clerkUserId)
    ? input.clerkUserId.trim()
    : null;

  return {
    distinctId: blockedJoinPostHogDistinctId({
      clerkUserId,
      visitorId: input.visitorId,
      postHogDistinctId: input.postHogDistinctId,
    }),
    event: BLOCKED_JOIN_POSTHOG_EVENTS[input.type],
    properties: {
      party_id: input.partyId,
      event_type: input.type,
      visitor_id: input.visitorId,
      user_id: clerkUserId,
      db_user_id: input.userId?.trim() || null,
    },
  };
}
